import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { receivedStudyService } from '../services/dataService';
import { useAuth } from '../hooks/useAuth';
import { hasPermission } from '../utils/permissions';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { KpiCard, percent } from '../components/DetailDashboard';
import ReceivedStudiesTable from '../components/ReceivedStudiesTable';
import { MATCH_STATUS } from '../utils/receivedStudies';
import styles from './assets/css/DetailDashboard.module.css';

const PAGE_SIZE = 20;

const ReceivedStudiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canDelete = hasPermission(user, 'received_studies', 'delete');
  const equipmentId = searchParams.get('equipment_id') || '';
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ match_status: '', modality: '', facility_id: '', search: '', start_date: '', end_date: '' });
  const [searchInput, setSearchInput] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [summaryKey, setSummaryKey] = useState(0);

  const { modality, facility_id: facilityId, search, start_date: startDate, end_date: endDate } = filters;
  const baseParams = useMemo(() => ({
    equipment_id: equipmentId || undefined,
    modality: modality || undefined,
    facility_id: facilityId || undefined,
    search: search || undefined,
    start_date: startDate ? `${startDate}T00:00:00` : undefined,
    end_date: endDate ? `${endDate}T23:59:59` : undefined,
  }), [equipmentId, modality, facilityId, search, startDate, endDate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedIds(new Set());
    try {
      setData(await receivedStudyService.list({
        ...baseParams,
        page,
        page_size: PAGE_SIZE,
        match_status: filters.match_status || undefined,
      }));
    } catch (err) {
      setError(err?.detail || 'Failed to load received studies');
    } finally {
      setLoading(false);
    }
  }, [baseParams, filters.match_status, page]);

  // Totals ignore the match filter and the page; the table still loads if they fail.
  useEffect(() => {
    let current = true;
    receivedStudyService.summary(baseParams)
      .then((result) => { if (current) setSummary(result); })
      .catch(() => { if (current) setSummary(null); });
    return () => { current = false; };
  }, [baseParams, summaryKey]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const noun = ids.length === 1 ? 'study' : 'studies';
    if (!window.confirm(
      `Delete the images of ${ids.length} ${noun} from the gateway? This cannot be undone. `
      + 'The study records are kept. The same study sent by another machine loses its images too.',
    )) return;

    setDeleting(true);
    setDeleteMessage('');
    setDeleteError('');
    try {
      const result = await receivedStudyService.deleteMany(ids);
      const deletedCount = result.deleted.length;
      if (deletedCount) setDeleteMessage(`Deleted images of ${deletedCount} ${deletedCount === 1 ? 'study' : 'studies'}.`);
      const problems = [
        ...result.failed.map((failure) => failure.error),
        ...(result.not_found.length ? [`${result.not_found.length} no longer exist or are not visible to you.`] : []),
      ];
      if (problems.length) setDeleteError(`Some studies' images were not deleted: ${[...new Set(problems)].join(' ')}`);
      await load();
      setSummaryKey((key) => key + 1);
    } catch (err) {
      setDeleteError(err?.detail || 'Failed to delete study images');
    } finally {
      setDeleting(false);
    }
  };

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  return (
    <div className={styles.page}>
      <div>
        <h1>Received Studies</h1>
        <p className={styles.muted}>
          Every study machines have sent to the gateway, with its patient, machine and facility, and whether it
          matches a request&apos;s worklist. Each row is one study from one machine; images and series are counted.
        </p>
      </div>

      {summary ? (
        <div className={styles.kpiGrid}>
          <KpiCard label="Received" value={summary.received} hint="Studies for these filters" />
          <KpiCard label="Matched" value={summary.matched} hint={`${percent(summary.matched, summary.received)}% of received`} />
          <KpiCard label="Possible match" value={summary.possible_match} hint={`${percent(summary.possible_match, summary.received)}% of received`} />
          <KpiCard label="Not matched" value={summary.unmatched} hint={`${percent(summary.unmatched, summary.received)}% of received`} />
        </div>
      ) : null}

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.filterRow}>
            <form onSubmit={(e) => { e.preventDefault(); setFilter('search', searchInput.trim()); }}>
              <input
                className={styles.filterInput}
                placeholder="Patient, accession, AE title or study UID"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </form>
            <select className={styles.filterInput} value={filters.match_status} onChange={(e) => setFilter('match_status', e.target.value)} aria-label="Match">
              <option value="">All matches</option>
              {Object.entries(MATCH_STATUS).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <SearchableSelect
              className={styles.filterInput}
              options={summary?.facilities || []}
              value={filters.facility_id}
              onChange={(value) => setFilter('facility_id', value)}
              emptyLabel="All facilities"
              ariaLabel="Facility"
            />
            <input
              className={styles.filterInput}
              placeholder="Modality (e.g. CT)"
              value={filters.modality}
              onChange={(e) => setFilter('modality', e.target.value.toUpperCase().trim())}
              maxLength={16}
              aria-label="Modality"
            />
            <input type="date" className={styles.filterInput} value={filters.start_date} onChange={(e) => setFilter('start_date', e.target.value)} aria-label="From" />
            <input type="date" className={styles.filterInput} value={filters.end_date} onChange={(e) => setFilter('end_date', e.target.value)} aria-label="To" />
          </div>
          {equipmentId ? (
            <button type="button" className={styles.secondaryButton} onClick={() => { setSearchParams({}); setPage(1); }}>
              Showing one machine · show all
            </button>
          ) : null}
        </div>

        {error ? <div className={styles.error}>{error}</div> : null}
        {deleteMessage ? <div className={styles.success}>{deleteMessage}</div> : null}
        {deleteError ? <div className={styles.error}>{deleteError}</div> : null}
        {canDelete && selectedIds.size ? (
          <div className={styles.selectionBar}>
            <span>{selectedIds.size} selected</span>
            <button type="button" className={styles.dangerButton} onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : `Delete images (${selectedIds.size})`}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={() => setSelectedIds(new Set())} disabled={deleting}>
              Clear selection
            </button>
          </div>
        ) : null}
        <ReceivedStudiesTable
          items={data.items}
          emptyText={loading ? 'Loading…' : 'No studies match these filters.'}
          selectedIds={canDelete ? selectedIds : undefined}
          onSelectionChange={canDelete ? setSelectedIds : undefined}
        />
        <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} loading={loading} onPageChange={setPage} />
      </section>
    </div>
  );
};

export default ReceivedStudiesPage;
